/**
 * The tenant app's step-up challenge: one broker, one dialog, every screen.
 *
 * `ERR_STEP_UP_REQUIRED` (CLAUDE.md §6/§13) is answered here rather than on the
 * screens that provoke it. The transport (`api/client.ts`) intercepts the 403,
 * asks this module for a code, and replays the request once the code is
 * accepted — so a payment run, a bank-detail change or a period close completes
 * instead of being thrown away with a message pointing at a profile page.
 *
 * This module deliberately imports nothing from `client.ts`. The client owns
 * the tokens and the generated operations, so *it* installs the verifier
 * (`stepUp.setVerifier`) at module load; the dialog installs the enrolment
 * route. Keeping the arrows one-way — client → stepup, dialog → stepup — is
 * what stops `client → stepup → client` becoming an undefined import at runtime.
 */

import { createStepUpBroker } from '@erpx/ui'

/** The app-wide challenge broker. The dialog renders `stepUp.prompt`. */
export const stepUp = createStepUpBroker()

/**
 * Which guarded action provoked the challenge, as a catalogue key suffix.
 *
 * "Paying suppliers needs your authenticator" is a sentence somebody can act
 * on; "Re-authentication required" is not. The transport only knows the method
 * and path, so the mapping lives here, keyed off the routes whose permissions
 * are declared `RequiresMFA` server-side (`ap.payment.execute`,
 * `ap.vendor.bank.*`, `cash.account.reveal`, `gl.period.close`).
 */
export type StepUpReason =
  | 'payment'
  | 'bankApprove'
  | 'reveal'
  | 'bankChange'
  | 'periodClose'
  | 'writeOff'
  | 'generic'

const ROUTES: readonly { pattern: RegExp; reason: StepUpReason }[] = [
  { pattern: /\/payment-runs\/[^/]+\/(execute|bank-file)$/, reason: 'payment' },
  { pattern: /\/vendor-bank-changes\/[^/]+\/decide$/, reason: 'bankApprove' },
  // Covers both reveals: a vendor's bank account and a company's own.
  { pattern: /\/bank-accounts\/[^/]+\/reveal$/, reason: 'reveal' },
  // A wallet's custodian and deputy personal data (R053).
  { pattern: /\/treasury-wallets\/[^/]+\/reveal-custodian$/, reason: 'reveal' },
  { pattern: /\/vendors\/[^/]+\/bank-accounts(\/|$)/, reason: 'bankChange' },
  // Closing a period is served by the foundation aliases (Period Control, FSD
  // 1.2.7, F-6.007); the `/fiscal-periods/:id/close|permanent-close` routes are
  // gone. Reopening is still `/fiscal-periods/:id/reopen` — it has no alias.
  { pattern: /\/foundation\/periods\/[^/]+\/(close|permanently-close)$/, reason: 'periodClose' },
  { pattern: /\/fiscal-periods\/[^/]+\/reopen$/, reason: 'periodClose' },
  // A write-off is posted or reversed under `ar.writeoff.post`, and its approval thresholds are
  // set under `ar.writeoff.approve`; both permissions are declared RequiresMFA (FT10-B34).
  { pattern: /\/ar-write-offs\/[^/]+\/(post|reverse)$/, reason: 'writeOff' },
  { pattern: /\/ar-write-off-settings$/, reason: 'writeOff' },
]

/**
 * The reason for a request path. Unknown paths get the generic wording rather
 * than a guess — a new guarded route that nobody remembered to add here still
 * produces a usable prompt, it just does not name the action.
 */
export function stepUpReason(path: string): StepUpReason {
  const withoutQuery = path.split('?')[0] ?? path
  for (const route of ROUTES) {
    if (route.pattern.test(withoutQuery)) return route.reason
  }
  return 'generic'
}
